import type { FC } from 'react'
import { Box, Card, CardContent, Skeleton, Typography } from '@mui/material'
import { useMembersOverview } from './MembersDashboardService'
import CareGapsPanel from './CareGapsPanel'
import ContractFilter from './ContractFilter'
import PriorityQueueCard from './PriorityQueueCard'
import ProductToggle from './ProductToggle'
import { useDashboardScope } from './queueRoutes'

// Ops "what needs attention" queues. Case-backed ones (overdue, awaiting-reply)
// gain a team filter in the drill-down; the cards here are always whole-tenant.
const QUEUES = [
  { key: 'overdue_cases', title: 'Overdue cases', tone: 'overdue' },
  { key: 'members_awaiting_reply', title: 'Members awaiting reply', tone: 'awaiting' },
  { key: 'onboarding_blocked', title: 'Onboarding blocked', tone: 'onboarding' },
  { key: 'hp_not_signed_up', title: 'Health plan · not signed up', tone: 'hp' },
] as const

const CARD_RADIUS = 2
const KPI_COUNT = 4

const fmt = (n: number) => n.toLocaleString()

const SectionLabel: FC<{ children: string }> = ({ children }) => (
  <Typography
    sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', color: 'grey.500', mb: 1.5 }}
  >
    {children.toUpperCase()}
  </Typography>
)

const MembersOverview: FC = () => {
  const { product, setProduct, contract, setContract } = useDashboardScope()
  const { data: overview, isPending: isLoading, error } = useMembersOverview(product, contract)

  const kpis = overview?.kpis

  // Self-describing tiles: absolute counts only (no denominators/percentages), so a
  // realtime board never invites comparison with the daily analytics stack.
  const kpiTiles = kpis
    ? [
        {
          label: 'Primary care established',
          value: kpis.primary_care_established,
          sub: 'Established with a Firefly PCP',
        },
        {
          label: 'Benefit enrolled',
          value: kpis.benefit_enrolled,
          sub:
            product === 'care_only' ? 'Not applicable to Care Only' : 'Active health plan benefit',
        },
        {
          label: 'Sent a chat message · 30d',
          value: kpis.sent_chat_30d,
          sub: 'Distinct members · async engagement',
        },
        {
          label: 'Checked-out visit · 30d',
          value: kpis.checked_out_visit_30d,
          sub: 'Distinct members · sync engagement',
        },
      ]
    : []

  return (
    <Box sx={{ p: 4, bgcolor: 'background.default', minHeight: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            Members
          </Typography>
          <Typography variant="body2" sx={{ color: 'grey.500', mt: 0.5 }}>
            Ops overview · what needs attention
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ContractFilter value={contract} onChange={setContract} />
          <ProductToggle value={product} onChange={setProduct} />
        </Box>
      </Box>

      {error != null ? (
        <Typography sx={{ color: 'error.main', mb: 4 }}>
          Failed to load the members overview.
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 2,
            mb: 4,
          }}
        >
          {isLoading
            ? Array.from({ length: KPI_COUNT }).map((_, i) => (
                <Card
                  key={i}
                  variant="outlined"
                  sx={{ borderColor: 'grey.100', borderRadius: CARD_RADIUS }}
                >
                  <CardContent>
                    <Skeleton variant="text" width="70%" />
                    <Skeleton variant="text" width="45%" height={34} sx={{ mt: 1 }} />
                    <Skeleton variant="text" width="90%" sx={{ mt: 1 }} />
                  </CardContent>
                </Card>
              ))
            : kpiTiles.map(tile => (
                <Card
                  key={tile.label}
                  variant="outlined"
                  sx={{ borderColor: 'grey.100', borderRadius: CARD_RADIUS }}
                >
                  <CardContent>
                    <Typography
                      sx={{
                        fontSize: 11,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        color: 'grey.500',
                        minHeight: 28,
                      }}
                    >
                      {tile.label}
                    </Typography>
                    <Typography sx={{ fontSize: 30, fontWeight: 500, lineHeight: 1, mt: 1 }}>
                      {fmt(tile.value)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'grey.400', display: 'block', mt: 1 }}
                    >
                      {tile.sub}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
        </Box>
      )}

      <Box sx={{ mb: 4 }}>
        <SectionLabel>Priority queues</SectionLabel>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
            gap: 2,
          }}
        >
          {QUEUES.map(q => (
            <PriorityQueueCard
              key={q.key}
              queueKey={q.key}
              title={q.title}
              product={product}
              contract={contract}
              tone={q.tone}
            />
          ))}
        </Box>
      </Box>

      <CareGapsPanel product={product} contract={contract} />
    </Box>
  )
}

export default MembersOverview
